// SPDX-License-Identifier: AGPL-3.0-or-later

import type {Channel} from '@app/features/channel/models/Channel';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import {makeAutoObservable, observable} from 'mobx';

export type MediaViewerItem = Readonly<{
	src: string;
	originalSrc: string;
	naturalWidth: number;
	naturalHeight: number;
	type: 'image' | 'gif' | 'gifv' | 'video' | 'audio';
	contentHash?: string | null;
	attachmentId?: string;
	embedIndex?: number;
	filename?: string;
	fileSize?: number;
	contentType?: string;
	duration?: number;
	expiresAt?: string | null;
	expired?: boolean;
	animated?: boolean;
	providerName?: string;
	initialTime?: number;
	/** KONVO: the message this item belongs to, when a viewer steps through pictures from several messages. */
	message?: Message;
	/** KONVO: `src` is a small version: reads the one that fills the screen. */
	loadView?: () => Promise<string | null>;
	/** KONVO: `src` is a small version: reads the original, for a deep zoom, saving and copying. */
	loadOriginal?: () => Promise<string | null>;
}>;

function copyMediaViewerItems(items: ReadonlyArray<MediaViewerItem>): ReadonlyArray<MediaViewerItem> {
	return Object.freeze(items.map((item) => Object.freeze({...item})));
}

function sameItem(a: MediaViewerItem, b: MediaViewerItem): boolean {
	const keys = new Set([...Object.keys(a), ...Object.keys(b)]) as Set<keyof MediaViewerItem>;
	for (const key of keys) if (a[key] !== b[key]) return false;
	return true;
}

class MediaViewer {
	isOpen: boolean = false;
	items: ReadonlyArray<MediaViewerItem> = [];
	currentIndex: number = 0;
	channelId?: string = undefined;
	messageId?: string = undefined;
	message?: Message = undefined;
	sourceChannel?: Channel = undefined;
	allowAttachmentDelete: boolean = false;
	/** KONVO: counts opens, so work finished after the viewer opened only changes the set it was for. */
	session: number = 0;

	constructor() {
		makeAutoObservable(
			this,
			{
				items: observable.ref,
				message: observable.ref,
				sourceChannel: observable.ref,
			},
			{autoBind: true},
		);
	}

	open(
		items: ReadonlyArray<MediaViewerItem>,
		currentIndex: number,
		channelId?: string,
		messageId?: string,
		message?: Message,
		sourceChannel?: Channel | null,
		allowAttachmentDelete: boolean = false,
	): void {
		this.isOpen = true;
		this.session += 1;
		this.items = copyMediaViewerItems(items);
		this.currentIndex = currentIndex;
		this.channelId = channelId;
		this.messageId = messageId;
		this.message = message;
		this.sourceChannel = sourceChannel ?? undefined;
		this.allowAttachmentDelete = allowAttachmentDelete;
	}

	/**
	 * KONVO: swaps in a fuller or better-measured set while the viewer stays open on the same
	 * picture. Items whose src is unchanged keep their object, so nothing on screen reloads.
	 */
	replaceItems(items: ReadonlyArray<MediaViewerItem>, currentIndex: number): void {
		if (!this.isOpen || items.length === 0) return;
		const kept = new Map(this.items.map((item) => [item.src, item]));
		this.items = Object.freeze(
			items.map((item) => {
				const same = kept.get(item.src);
				return same && sameItem(same, item) ? same : Object.freeze({...item});
			}),
		);
		this.currentIndex = Math.min(Math.max(currentIndex, 0), items.length - 1);
	}

	close(): void {
		this.isOpen = false;
		this.items = [];
		this.currentIndex = 0;
		this.channelId = undefined;
		this.messageId = undefined;
		this.message = undefined;
		this.sourceChannel = undefined;
		this.allowAttachmentDelete = false;
	}

	navigate(index: number): void {
		if (index < 0 || index >= this.items.length) {
			return;
		}
		this.currentIndex = index;
	}

	getCurrentItem(): MediaViewerItem | undefined {
		if (!this.isOpen || this.items.length === 0) {
			return;
		}
		return this.items[this.currentIndex];
	}

	canNavigatePrevious(): boolean {
		return this.isOpen && this.currentIndex > 0;
	}

	canNavigateNext(): boolean {
		return this.isOpen && this.currentIndex < this.items.length - 1;
	}
}

export default new MediaViewer();
